import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import {
  Loader2,
  PenSquare,
  ShieldCheck,
  Plus,
  Edit3,
  FileText,
  Clock,
  User,
  Calendar,
  Download,
  Upload,
  Search,
  Filter,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Separator } from "../ui/separator";
import {
  fetchSoapNotes,
  createSoapNote,
  updateSoapNote,
  signSoapNote,
  fetchClients,
  fetchAppointments,
  improveSoapText,
} from "../../lib/therapistApi";

const defaultForm = {
  id: null,
  clientName: "",
  clientEmail: "",
  appointmentId: "",
  subjective: "",
  objective: "",
  assessment: "",
  plan: "",
  attachments: [],
  isDraft: true,
};

const STATUS_CONFIG = {
  draft: {
    label: "Draft",
    badgeClass: "border-amber-200 bg-amber-50 text-amber-600",
  },
  notYetSigned: {
    label: "Not yet signed",
    badgeClass: "border-indigo-200 bg-indigo-50 text-indigo-600",
  },
  signed: {
    label: "Signed",
    badgeClass: "border-emerald-200 bg-emerald-50 text-emerald-600",
  },
};

const STATUS_PRIORITY = {
  draft: 0,
  notYetSigned: 1,
  signed: 2,
};

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "notYetSigned", label: "Not yet signed" },
  { value: "signed", label: "Signed" },
];

function valueToString(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
}

function stringToValue(value) {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  try {
    return JSON.parse(trimmed);
  } catch (error) {
    return trimmed;
  }
}

function useTherapistId() {
  const { therapistId } = useAuth();
  return therapistId;
}

const parseDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "number") {
    const asDate = new Date(value);
    return Number.isNaN(asDate.getTime()) ? null : asDate;
  }
  if (typeof value === "string") {
    try {
      const parsed = parseISO(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    } catch (error) {
      return null;
    }
  }
  return null;
};

const safeFormatDateTime = (value, fallback = "") => {
  const parsed = parseDate(value);
  return parsed ? format(parsed, "MMM d, h:mm a") : fallback;
};

const getClientDisplayName = (client) => {
  if (!client) return "Client";
  const nameFromFields =
    client.fullName ||
    client.full_name ||
    [client.firstName, client.lastName].filter(Boolean).join(" ") ||
    [client.first_name, client.last_name].filter(Boolean).join(" ") ||
    client.name ||
    client.clientName ||
    client.client_name;

  return nameFromFields || client.email || client.emailAddress || "Client";
};

const getClientKeyFromClient = (client) =>
  client?.clientId ||
  client?.client_id ||
  client?.id ||
  client?._id ||
  client?.email ||
  client?.emailAddress ||
  client?.email_address ||
  client?.uuid ||
  getClientDisplayName(client);

const getClientKeyFromNote = (note) =>
  note?.clientId ||
  note?.client_id ||
  note?.client ||
  note?.clientEmail ||
  note?.client_email ||
  note?.clientName ||
  note?.client_name ||
  note?._id;

const getNoteStatus = (note) => {
  if (note?.signedAt) return "signed";
  if (note?.isDraft) return "draft";
  return "notYetSigned";
};

const getNoteTimestamp = (note) => {
  const candidates = [note?.updatedAt, note?.createdAt, note?.signedAt];
  for (const value of candidates) {
    const parsed = parseDate(value);
    if (parsed) {
      return parsed.getTime();
    }
  }
  return -Infinity;
};

const pickLatestNote = (notes) => {
  if (!notes.length) return null;
  return notes.reduce((latest, note) => {
    if (!latest) return note;
    return getNoteTimestamp(note) > getNoteTimestamp(latest) ? note : latest;
  }, null);
};

const deriveEntryStatus = (notes) => {
  if (!notes.length) return "notYetSigned";
  let bestStatus = "signed";
  let bestPriority = Infinity;
  notes.forEach((note) => {
    const status = getNoteStatus(note);
    const priority = STATUS_PRIORITY[status] ?? Number.POSITIVE_INFINITY;
    if (priority < bestPriority) {
      bestStatus = status;
      bestPriority = priority;
    }
  });
  return bestStatus;
};

const SOAPNotes = () => {
  const therapistId = useTherapistId();
  const [notes, setNotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedClientKey, setSelectedClientKey] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isClientAutoSelected, setIsClientAutoSelected] = useState(false);
  const [aiLoading, setAiLoading] = useState({
    subjective: false,
    objective: false,
    assessment: false,
    plan: false,
  });

  const loadNotes = useCallback(async () => {
    if (!therapistId) return;
    try {
      setLoading(true);
      const res = await fetchSoapNotes(therapistId);
      setNotes(res.notes || []);
    } catch (error) {
      toast.error(error?.message || "Failed to load SOAP notes");
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [therapistId]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  useEffect(() => {
    if (!therapistId) return;
    (async () => {
      try {
        const [clientsRes, appointmentsRes] = await Promise.all([
          fetchClients(therapistId),
          fetchAppointments(therapistId),
        ]);

        const clientsList =
          clientsRes?.clients || clientsRes?.data || clientsRes || [];
        setClients(Array.isArray(clientsList) ? clientsList : []);

        const appointmentsList =
          appointmentsRes?.appointments ||
          appointmentsRes?.data ||
          appointmentsRes ||
          [];
        // Filter to only show completed appointments for SOAP notes
        const completedAppointments = Array.isArray(appointmentsList)
          ? appointmentsList.filter(apt => apt.status === 'completed')
          : [];
        setAppointments(completedAppointments);
      } catch (error) {
        console.warn("Failed to load client list for SOAP notes", error);
      }
    })();
  }, [therapistId]);

  const selectNote = (note) => {
    setForm({
      id: note._id,
      clientName: note.clientName || note.client_name || "",
      clientEmail: note.clientEmail || note.client_email || "",
      appointmentId: note.appointment || "",
      subjective: valueToString(note.subjective),
      objective: valueToString(note.objective),
      assessment: valueToString(note.assessment),
      plan: valueToString(note.plan),
      attachments: note.attachments || [],
      isDraft: note.isDraft ?? !note.signedAt,
    });
    const entryKey =
      getClientKeyFromNote(note) ||
      note.clientName ||
      note.client_name ||
      note.clientEmail ||
      note.client_email ||
      note._id;
    setSelectedClientKey(entryKey);
  };

  const resetForm = () => {
    setForm(defaultForm);
    setIsClientAutoSelected(false);
  };

  const handleChange = (key) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!therapistId) return;

    const payload = {
      clientName: form.clientName || undefined,
      clientEmail: form.clientEmail || undefined,
      appointmentId: form.appointmentId || undefined,
      subjective: stringToValue(form.subjective),
      objective: stringToValue(form.objective),
      assessment: stringToValue(form.assessment),
      plan: stringToValue(form.plan),
      attachments: form.attachments || [],
      isDraft: form.isDraft,
    };

    try {
      setSaving(true);
      if (form.id) {
        await updateSoapNote(therapistId, form.id, payload);
        toast.success("SOAP note updated");
      } else {
        await createSoapNote(therapistId, payload);
        toast.success("SOAP note created");
      }
      await loadNotes();
      resetForm();
      setSelectedClientKey(null);
    } catch (error) {
      toast.error(error?.message || "Failed to save SOAP note");
    } finally {
      setSaving(false);
    }
  };

  const handleSign = async (noteId) => {
    try {
      await signSoapNote(therapistId, noteId);
      toast.success("SOAP note signed");
      await loadNotes();
      if (form.id === noteId) {
        resetForm();
        setSelectedClientKey(null);
      }
    } catch (error) {
      toast.error(error?.message || "Unable to sign SOAP note");
    }
  };

  const handleAiImprove = async (section) => {
    const currentText = form[section];
    if (!currentText || !currentText.trim()) {
      toast.error("Please enter some text first");
      return;
    }

    setAiLoading((prev) => ({ ...prev, [section]: true }));
    try {
      const response = await improveSoapText(currentText, section);
      setForm((prev) => ({ ...prev, [section]: response.improved }));
      toast.success("Text improved with AI");
    } catch (error) {
      toast.error(error?.message || "Failed to improve text with AI");
    } finally {
      setAiLoading((prev) => ({ ...prev, [section]: false }));
    }
  };

  const drafts = useMemo(() => notes.filter((note) => !note.signedAt), [notes]);
  const signed = useMemo(() => notes.filter((note) => note.signedAt), [notes]);

  const clientEntries = useMemo(() => {
    const entries = new Map();

    const ensureEntry = (key, name) => {
      const resolvedKey = key || name || `unknown-${entries.size}`;
      if (!entries.has(resolvedKey)) {
        entries.set(resolvedKey, {
          key: resolvedKey,
          name: name || "Client",
          notes: [],
          latestNote: null,
          lastUpdated: null,
          status: "notYetSigned",
        });
      } else if (name && entries.get(resolvedKey).name === "Client") {
        entries.get(resolvedKey).name = name;
      }
      return entries.get(resolvedKey);
    };

    clients.forEach((client) => {
      const key = getClientKeyFromClient(client);
      const entry = ensureEntry(key, getClientDisplayName(client));
      entry.client = client;
    });

    notes.forEach((note) => {
      const key = getClientKeyFromNote(note);
      const displayName =
        note.clientName ||
        note.client_name ||
        note.clientEmail ||
        note.client_email;
      const entry = ensureEntry(key, displayName || "Client");
      entry.notes.push(note);
    });

    entries.forEach((entry) => {
      entry.status = deriveEntryStatus(entry.notes);
      entry.latestNote = pickLatestNote(entry.notes);
      entry.lastUpdated = entry.latestNote
        ? getNoteTimestamp(entry.latestNote)
        : null;
      if ((!entry.name || entry.name === "Client") && entry.latestNote) {
        entry.name =
          entry.latestNote.clientName ||
          entry.latestNote.client_name ||
          entry.latestNote.clientEmail ||
          entry.latestNote.client_email ||
          "Client";
      }
    });

    return Array.from(entries.values()).sort((a, b) => {
      const statusDiff =
        (STATUS_PRIORITY[a.status] ?? 99) - (STATUS_PRIORITY[b.status] ?? 99);
      if (statusDiff !== 0) return statusDiff;
      if (a.lastUpdated && b.lastUpdated) return b.lastUpdated - a.lastUpdated;
      if (a.lastUpdated) return -1;
      if (b.lastUpdated) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [clients, notes]);

  const filteredEntries = useMemo(() => {
    let filtered = clientEntries;

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((entry) => entry.status === statusFilter);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (entry) =>
          entry.name.toLowerCase().includes(query) ||
          (entry.client?.email &&
            entry.client.email.toLowerCase().includes(query)),
      );
    }

    return filtered;
  }, [clientEntries, statusFilter, searchQuery]);

  const handleSelectClient = (entry) => {
    setSelectedClientKey(entry.key);
    if (entry.latestNote) {
      selectNote(entry.latestNote);
      setIsClientAutoSelected(false);
      return;
    }
    // Use getClientDisplayName to get the actual name from client object, not the fallback email
    const clientName = entry.client
      ? getClientDisplayName(entry.client)
      : (entry.name || "");

    setForm({
      ...defaultForm,
      clientName: clientName,
      clientEmail: entry.client?.email || "",
    });
    setIsClientAutoSelected(true);
  };

  const handleSelectAppointment = (appointment) => {
    setForm((prev) => ({
      ...prev,
      appointmentId: appointment.id,
      clientName: appointment.clientName || "",
      clientEmail: appointment.clientEmail || "",
    }));
  };

  const getInitials = (name) => {
    if (!name) return "C";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  if (!therapistId) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-sm text-gray-600">
        Therapist ID missing. Log in again to continue.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2">
                SOAP Notes
              </h1>
              <p className="text-slate-600 text-lg">
                Comprehensive documentation for each therapy session
              </p>
              <div className="mt-3 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <ShieldCheck className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-700">
                  SOAP notes can only be created for appointments marked as <strong>completed</strong> by clinic staff.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-sm text-slate-500">Total Notes</div>
                <div className="text-2xl font-bold text-slate-900">
                  {notes.length}
                </div>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <FileText className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">
                    Draft Notes
                  </p>
                  <p className="text-2xl font-bold text-amber-600">
                    {drafts.length}
                  </p>
                </div>
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <PenSquare className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">
                    Signed Notes
                  </p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {signed.length}
                  </p>
                </div>
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <ShieldCheck className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">
                    Active Clients
                  </p>
                  <p className="text-2xl font-bold text-indigo-600">
                    {clientEntries.length}
                  </p>
                </div>
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <User className="h-5 w-5 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">
                    Pending Review
                  </p>
                  <p className="text-2xl font-bold text-orange-600">
                    {
                      clientEntries.filter((e) => e.status === "notYetSigned")
                        .length
                    }
                  </p>
                </div>
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid gap-8 lg:grid-cols-[350px,1fr,300px]">
          {/* Client List */}
          <Card className="bg-white border-0 shadow-lg">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-slate-900">
                  Clients
                </CardTitle>
                <User className="h-5 w-5 text-slate-400" />
              </div>

              {/* Search */}
              <div className="relative mt-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-50 border-slate-200"
                />
              </div>

              {/* Status Filters */}
              <div className="flex flex-wrap gap-2 mt-4">
                {STATUS_FILTERS.map((option) => (
                  <Button
                    key={option.value}
                    type="button"
                    size="sm"
                    variant={
                      statusFilter === option.value ? "default" : "outline"
                    }
                    onClick={() => setStatusFilter(option.value)}
                    className={`text-xs ${
                      statusFilter === option.value
                        ? "bg-indigo-600 hover:bg-indigo-700"
                        : "border-slate-200 hover:border-indigo-300"
                    }`}>
                    {option.label}
                  </Button>
                ))}
              </div>
            </CardHeader>

            <CardContent className="space-y-3 max-h-96 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
                </div>
              ) : filteredEntries.length ? (
                filteredEntries.map((entry) => {
                  const statusMeta =
                    STATUS_CONFIG[entry.status] || STATUS_CONFIG.notYetSigned;
                  const isSelected = selectedClientKey === entry.key;
                  return (
                    <button
                      type="button"
                      key={entry.key}
                      onClick={() => handleSelectClient(entry)}
                      className={`w-full rounded-lg border p-4 text-left transition-all duration-200 ${
                        isSelected
                          ? "border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200"
                          : "border-slate-200 hover:border-indigo-300 hover:bg-slate-50"
                      }`}>
                      <div className="flex items-start gap-3">
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="bg-indigo-100 text-indigo-600 text-xs">
                            {getInitials(entry.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <h4 className="text-sm font-medium text-slate-900 truncate">
                              {entry.name}
                            </h4>
                            <Badge
                              variant="outline"
                              className={`text-xs ${statusMeta.badgeClass}`}>
                              {statusMeta.label}
                            </Badge>
                          </div>
                          {entry.latestNote ? (
                            <p className="text-xs text-slate-500">
                              Updated{" "}
                              {safeFormatDateTime(
                                entry.latestNote.updatedAt ||
                                  entry.latestNote.createdAt,
                              )}
                            </p>
                          ) : (
                            <p className="text-xs text-slate-400">
                              No notes yet
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="text-center py-8">
                  <User className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No clients found</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* SOAP Note Form */}
          <Card className="bg-white border-0 shadow-lg">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-slate-900">
                  {form.id ? "Edit SOAP Note" : "Create SOAP Note"}
                </CardTitle>
                {form.id && (
                  <Badge variant="outline" className="text-xs">
                    v{form.version || 1}
                  </Badge>
                )}
              </div>
            </CardHeader>

            <CardContent>
              <form className="space-y-6" onSubmit={handleSubmit}>
                {/* Client Information */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Client Name
                  </label>
                  <Input
                    value={form.clientName}
                    onChange={handleChange("clientName")}
                    placeholder="Enter client name"
                    className="bg-slate-50 border-slate-200"
                    readOnly={isClientAutoSelected}
                    disabled={isClientAutoSelected}
                  />
                </div>

                {/* SOAP Sections */}
                {[
                  [
                    "subjective",
                    "Subjective",
                    "Patient's description of symptoms, concerns, and history",
                  ],
                  [
                    "objective",
                    "Objective",
                    "Observable and measurable findings",
                  ],
                  [
                    "assessment",
                    "Assessment",
                    "Clinical impression and diagnosis",
                  ],
                  ["plan", "Plan", "Treatment goals and next steps"],
                ].map(([key, label, description]) => (
                  <div key={key}>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-slate-700">
                        {label}
                      </label>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleAiImprove(key)}
                        disabled={aiLoading[key] || !form[key]?.trim()}
                        className="h-7 px-2 gap-1 border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50 text-indigo-600">
                        {aiLoading[key] ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Wand2 className="h-3 w-3" />
                        )}
                        <span className="text-xs">AI Magic</span>
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">{description}</p>
                    <Textarea
                      value={form[key]}
                      onChange={handleChange(key)}
                      rows={5}
                      placeholder={`Enter ${label.toLowerCase()} notes...`}
                      className="bg-slate-50 border-slate-200 resize-none"
                    />
                  </div>
                ))}

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                  <label className="flex items-center gap-3 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={form.isDraft}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          isDraft: event.target.checked,
                        }))
                      }
                      className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                    />
                    Save as draft
                  </label>

                  <div className="flex gap-3">
                    {form.id && !form.isDraft && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleSign(form.id)}
                        className="border-slate-200 hover:border-indigo-300">
                        <ShieldCheck className="h-4 w-4 mr-2" />
                        Sign Note
                      </Button>
                    )}
                    <Button
                      type="submit"
                      disabled={saving}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-6">
                      {saving && (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      )}
                      <Edit3 className="h-4 w-4 mr-2" />
                      {form.id ? "Update Note" : "Create Note"}
                    </Button>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Recent Notes Sidebar */}
          <div className="space-y-6">
            {/* Drafts */}
            <Card className="bg-white border-0 shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <PenSquare className="h-4 w-4 text-amber-600" />
                  Draft Notes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-64 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-amber-500" />
                  </div>
                ) : drafts.length ? (
                  drafts.slice(0, 5).map((note) => (
                    <button
                      type="button"
                      key={note._id}
                      onClick={() => selectNote(note)}
                      className="w-full text-left rounded-lg border border-amber-200 bg-amber-50 p-3 transition hover:border-amber-300 hover:bg-amber-100">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-amber-800">
                          Draft v{note.version}
                        </span>
                        <span className="text-xs text-amber-600">
                          {safeFormatDateTime(note.updatedAt)}
                        </span>
                      </div>
                      <div className="text-xs text-amber-700 truncate">
                        {note.clientName || note.clientEmail || "Client"}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <PenSquare className="h-6 w-6 text-amber-300 mx-auto mb-2" />
                    <p className="text-xs text-amber-600">No drafts pending</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Signed Notes */}
            <Card className="bg-white border-0 shadow-lg">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-emerald-600" />
                  Recent Signed
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-64 overflow-y-auto">
                {signed.length ? (
                  signed.slice(0, 5).map((note) => (
                    <div
                      key={note._id}
                      className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-emerald-800">
                          v{note.version}
                        </span>
                        <span className="text-xs text-emerald-600">
                          {safeFormatDateTime(note.signedAt)}
                        </span>
                      </div>
                      <div className="text-xs text-emerald-700 truncate">
                        {note.clientName || note.clientEmail || "Client"}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <ShieldCheck className="h-6 w-6 text-emerald-300 mx-auto mb-2" />
                    <p className="text-xs text-emerald-600">
                      No signed notes yet
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SOAPNotes;
